import * as React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@rocco/components/avatar';
import { LoaderCircle } from 'lucide-react';
import type { Comment, Entity } from '@modules/build-hub-comment/types';
import { timeToNow } from '@modules/date-time/util/utc-date-to-local-timezone';
import { getInitials } from '@modules/build-hub/utils/get-initials';
import { cn } from '@rocco/utils/cn';
import {
    decodeMessage,
    toEntities,
} from '@modules/build-hub-comment/utils/comment-parser';
import { useBuildHubData } from '@modules/build-hub/hooks/use-build-hub-data';

/**
 * Render a message with mentions styled in blue.
 * Similar to CommentViewer but optimized for preview display.
 */
function renderMessageWithMentions(
    rawMessage: string,
    entities: Entity[]
): React.ReactNode {
    const decoded = decodeMessage(rawMessage, entities);
    const sortedEntities = [...entities].sort(
        (a, b) => b.label.length - a.label.length
    );

    let parts: (string | JSX.Element)[] = [decoded];

    for (const ent of sortedEntities) {
        const next: (string | JSX.Element)[] = [];
        const needle = `@${ent.label}`;

        parts.forEach((p, i) => {
            if (typeof p === 'string') {
                const split = p.split(needle);
                split.forEach((chunk, j) => {
                    if (chunk) next.push(chunk);
                    if (j < split.length - 1) {
                        next.push(
                            <span
                                key={`${ent.type}:${ent.id}:${i}:${j}`}
                                className="font-medium text-[#60a5fa]"
                            >
                                {needle}
                            </span>
                        );
                    }
                });
            } else {
                next.push(p);
            }
        });

        parts = next;
    }

    return <>{parts}</>;
}

interface CommentPreviewProps {
    /** Array of comments to display */
    comments: Comment[];
    /** Total count of comments (may be higher than comments array length) */
    commentsCount: number;
    /** Loading state while fetching comments */
    isLoading?: boolean;
    /** Callback when the preview is clicked */
    onClick?: () => void;
}

/**
 * Displays a Figma-style preview of comments in a hover popover.
 *
 * Features:
 * - Horizontal scrollable row of unique user avatars (deduplicated by author ID)
 * - First (oldest chronologically) comment with author name and relative timestamp
 * - Total comment count
 * - Clickable to open full comment drawer
 * - Accessible with keyboard navigation
 *
 * @example
 * ```tsx
 * <CommentPreview
 *   comments={[...]}
 *   commentsCount={5}
 *   onClick={() => openDrawer()}
 * />
 * ```
 */
export function CommentPreview({
    comments,
    commentsCount,
    isLoading,
    onClick,
}: CommentPreviewProps) {
    const { buildHub } = useBuildHubData();

    if (isLoading) {
        return (
            <div className="flex min-w-[280px] items-center justify-center py-8">
                <LoaderCircle className="size-5 animate-spin text-gray-400" />
            </div>
        );
    }

    if (comments.length === 0) {
        return (
            <div className="min-w-[280px] py-4 text-center text-sm text-gray-500">
                No comments yet
            </div>
        );
    }

    // Get unique authors by ID
    const uniqueAuthors = Array.from(
        new Map(
            comments.map((comment) => [comment.author.id, comment.author])
        ).values()
    );

    // Get first (oldest) comment
    const sortedComments = [...comments].sort(
        (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    const firstComment = sortedComments[0];

    // Get entities for rendering mentions
    const messageEntities = firstComment
        ? toEntities(firstComment.mentions, firstComment.files, buildHub.owner)
        : [];

    return (
        <div
            className={cn(
                'min-w-[280px] max-w-[320px] space-y-2.5 rounded-xl border border-gray-200 bg-white p-4 shadow-md',
                onClick && 'cursor-pointer transition-colors hover:bg-gray-50'
            )}
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={
                onClick
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onClick();
                          }
                      }
                    : undefined
            }
        >
            {/* Overlapping avatars */}
            <div className="flex items-center">
                <div className="flex -space-x-2">
                    {uniqueAuthors.slice(0, 3).map((author, index) => (
                        <Avatar
                            key={author.id}
                            size="square32"
                            border="light"
                            className="ring-2 ring-white"
                            style={{ zIndex: uniqueAuthors.length - index }}
                        >
                            <AvatarImage
                                src={author.avatar ?? undefined}
                                alt={author.name}
                            />
                            <AvatarFallback>
                                {author.name ? getInitials(author.name) : '?'}
                            </AvatarFallback>
                        </Avatar>
                    ))}
                    {uniqueAuthors.length > 3 && (
                        <div className="flex size-8 items-center justify-center rounded-full border-2 border-white bg-gray-200 text-xs font-medium text-gray-600 ring-2 ring-white">
                            +{uniqueAuthors.length - 3}
                        </div>
                    )}
                </div>
            </div>

            {/* First comment */}
            {firstComment && (
                <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <span className="font-medium text-gray-900">
                            {firstComment.author.name ||
                                firstComment.author.email}
                        </span>
                        <span>•</span>
                        <span>{timeToNow(firstComment.createdAt)}</span>
                    </div>
                    <div className="line-clamp-2 text-sm leading-snug text-gray-700">
                        {renderMessageWithMentions(
                            firstComment.message,
                            messageEntities
                        )}
                    </div>
                </div>
            )}

            {/* Comment count footer */}
            <div className="text-xs text-gray-400">
                {commentsCount} {commentsCount === 1 ? 'reply' : 'replies'}
            </div>
        </div>
    );
}
